'use client';

import { type ReactNode, useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import {
  buildSaleListingHref,
  buildSaleLotAnimalHref,
} from '@/lib/saleCataloguePaths';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { SEARCH_TYPE } from '@/constants/searchType';
import { useSaleIdParam } from '@/hooks/publicSearch/useSaleIdParam';
import { SaleCatalogueNameLink } from '@/components/publicSearch/sale-catalogues/SaleCatalogueNameLink';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { MobileCardLinkRow } from '@/hooks/publicSearch/sharedHelpers';
import NameCell from '@/components/publicSearch/resultCell/NameCell';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';
import type {
  GetSalesRow,
  GetSaleLotsRow,
} from '@/components/publicSearch/constants';
import { sanitizeSaleLotIdent } from '@/hooks/publicSearch/sale-catalogue/resolveSaleLotDisplayName';

/**
 * Shared mobile card body for the sale and sale-lot list rows: a leading badge,
 * a primary (info-coloured) title line, and an optional secondary subtitle line.
 * `truncateSubtitle` matches the per-row subtitle behaviour (single-line ellipsis
 * for sale-lot rows, natural wrapping for sale rows).
 */
function SaleCardBody({
  badge,
  primary,
  subtitle,
  truncateSubtitle = false,
}: {
  badge: ReactNode;
  primary: ReactNode;
  subtitle?: string;
  truncateSubtitle?: boolean;
}) {
  return (
    <>
      {badge}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            color: 'info.main',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.25,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {primary}
        </Typography>
        {subtitle && (
          <Typography
            component="div"
            sx={{
              color: 'text.secondary',
              fontSize: '12px',
              lineHeight: 1.35,
              ...(truncateSubtitle
                ? {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }
                : {}),
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </>
  );
}

function SaleIconBadge() {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        color: theme.palette.primary.main,
      }}
    >
      <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
    </Box>
  );
}

/**
 * DataGrid cell renderer for the sale name column.
 * On desktop returns a plain link; on mobile renders a compact list row
 * showing sale name, date, lot count, and sale type as subtitle lines.
 */
export function SaleListRow({ row }: { row: GetSalesRow }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const params = useParams<{ orgName: string }>();
  const searchParams = useSearchParams();

  const href = useMemo(() => {
    const org = params.orgName ?? '';
    if (!org) return '#';
    const q = new URLSearchParams(searchParams.toString());
    q.delete(SEARCH_PARAM.SEARCH_TYPE);
    q.set(SEARCH_PARAM.SALE_ID, String(row.ID));
    return buildSocietySearchHref(
      org,
      SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
      q,
    );
  }, [params.orgName, searchParams, row.ID]);

  if (!isMobile) {
    return <SaleCatalogueNameLink row={row} />;
  }

  const subtitle = [
    row.SALE_DATE,
    row.LOT_COUNT != null ? `${row.LOT_COUNT} lots` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <MobileCardLinkRow href={href}>
      <SaleCardBody
        badge={<SaleIconBadge />}
        primary={row.SALE_NAME || 'Sale'}
        subtitle={subtitle}
      />
    </MobileCardLinkRow>
  );
}

/**
 * DataGrid cell renderer for the animal name column on the sale/semen catalogue detail page.
 * On desktop returns a plain NameCell link; on mobile renders a compact row showing
 * animal name, lot number, and identifier as subtitle lines.
 *
 * Lots without a numeric animal id (i4 DLOT / no-ident herd bulls) still render the
 * lot name. They link back to the sale listing anchored on that lot when possible,
 * matching Internet Solutions catalogue visibility for unregistered sale entries.
 */
export function SaleLotListRow({
  orgCode,
  row,
}: {
  orgCode: string;
  row: GetSaleLotsRow;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchParams = useSearchParams();
  const saleId = useSaleIdParam();
  const id = row.ID;
  const name = displayPlainText(row.ANIMAL_NAME);
  const identifier = sanitizeSaleLotIdent(row.ANIMAL_IDENT);
  const sex = row.SEX ?? '';
  const lot = row.LOT_NO ?? '';
  const label = name || identifier || (lot ? `Lot ${lot}` : 'Animal');

  let path = '';
  if (id) {
    if (saleId && lot) {
      path = buildSaleLotAnimalHref(orgCode, id, {
        saleId,
        lotNo: lot,
        searchParams,
      });
    } else {
      path = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${id}`;
    }
  } else if (saleId) {
    path = buildSaleListingHref(orgCode, saleId, searchParams, {
      lotNo: lot || undefined,
    });
  }

  if (!isMobile) {
    if (!path) {
      return (
        <Typography
          component="span"
          sx={{ fontWeight: 500, color: 'text.primary', px: 1.25, py: 0.5 }}
        >
          {label}
        </Typography>
      );
    }
    return <NameCell name={label} path={path} />;
  }

  const subtitleParts = [lot ? `Lot ${lot}` : null, identifier || null].filter(
    Boolean,
  );
  const subtitle = subtitleParts.join(' · ');

  if (!path) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
      >
        <SaleCardBody
          badge={<AnimalSexBadge sex={sex} />}
          primary={label}
          subtitle={subtitle}
          truncateSubtitle
        />
      </Box>
    );
  }

  return (
    <MobileCardLinkRow href={path}>
      <SaleCardBody
        badge={<AnimalSexBadge sex={sex} />}
        primary={label}
        subtitle={subtitle}
        truncateSubtitle
      />
    </MobileCardLinkRow>
  );
}
